import { NoteElementType, NoteType } from "@/types/NotesTypes";
import { makeAutoObservable, runInAction } from "mobx";
import { LoadableMixIn } from "./LoadableMixIn";
import { universalServices } from "@/services/UniversalServices";
import { addNotification } from "@/utils/addNotification";
import { FullOverlayModalsControl } from "./FullOverlayModalsControl";
import { WaitingUtils } from "./WaitingUtils";
import { userStore } from "./UserStore";

export class Note {
  id: number = 0;
  title: string = "";
  version: number = 0;
  date_create: string = "";
  viewers: string[] = [];
  elements: NoteElementType[] = [];

  constructor(data: NoteType) {
    this.id = data.id;
    this.title = data.title;
    this.version = data.version;
    this.date_create = data.date_create;
    this.viewers = data.viewers;
    this.elements = data.elements;

    makeAutoObservable(this);
  }
}

export class NotesStore {
  notes: Note[] = [];
  instanceLoadable = new LoadableMixIn(this);

  constructor() {
    makeAutoObservable(this);
  }

  get dataCount() {
    return this.notes.length;
  }

  async setData() {
    try {
      const result = await universalServices.getData<{ notes: NoteType[] }>("patches_note", "get-notes");

      if (!result.success) {
        addNotification({ type: "error", message: result.message });
        return;
      }

      const incoming = result.data?.notes || [];

      runInAction(() => {
        this.notes = incoming.map((item) => new Note(item));
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";

      addNotification({ type: "error", message });
    }
  }

  appendNotes(notes: NoteType[]) {
    this.notes = [...notes.reverse().map((item) => new Note(item)), ...this.notes];
  }
}

export const notesStore = new NotesStore();

////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////

export class FormNotesStore {
  notes: Note[] = [];
  modal: FullOverlayModalsControl = new FullOverlayModalsControl();

  constructor() {
    makeAutoObservable(this);
  }

  removeNote(id: number) {
    this.notes = this.notes.filter((item) => item.id !== id);
  }

  removeNoteElement(idNote: number, idEl: number) {
    this.notes = this.notes.map((item) => {
      if (item.id === idNote) {
        item.elements = item.elements.filter((el) => el.id !== idEl);
      }
      return item;
    });
  }

  addNote() {
    this.notes.push(
      new Note({ id: Date.now(), title: "", version: 0, date_create: "", viewers: [], elements: [] }),
    );
  }

  addNoteElement(idNote: number, typeElement: "text" | "image") {
    this.notes = this.notes.map((item) => {
      if (item.id === idNote) {
        item.elements.push({ id: Date.now(), content: "", type: typeElement });
      }
      return item;
    });
  }

  updateTitle(id: number, title: string) {
    this.notes = this.notes.map((item) => {
      if (item.id === id) {
        item.title = title;
      }
      return item;
    });
  }

  updateValueElement(
    idNote: number,
    idElement: number,
    typeField: "text" | "image" | "preview",
    value: string | File | null,
  ) {
    this.notes = this.notes.map((item) => {
      if (item.id === idNote) {
        item.elements = item.elements.map((el) => {
          if (el.id === idElement) {
            if (typeField === "text") {
              el.content = value as string;
            }
            if (typeField === "image") {
              el.file = value as File | null;
            }
            if (typeField === "preview") {
              el.preview = value as string | null;
            }
          }
          return el;
        });
      }
      return item;
    });
  }

  save = () => {
    utilsNotes.save();
  };

  buildFormData(notes: Note[]): FormData {
    const formData = new FormData();

    const preparedNotes = notes.map((note, noteIndex) => ({
      title: note.title,
      version: note.version,
      elements: note.elements.map((el, elIndex) => {
        if (el.file) {
          const key = `file_${noteIndex}_${elIndex}`;

          formData.append(key, el.file);

          return {
            type: el.type,
            content: el.content ?? "",
            fileKey: key,
          };
        }

        return {
          type: el.type,
          content: el.content ?? "",
        };
      }),
    }));

    formData.append("notes", JSON.stringify(preparedNotes));

    return formData;
  }

  clear() {
    this.notes = [];
  }
}

export const formNotesStore = new FormNotesStore();

class UtilsNotes extends WaitingUtils {
  constructor() {
    super();
  }

  //eslint-disable-next-line
  async save(): Promise<any> {
    const formData = formNotesStore.buildFormData(formNotesStore.notes);
    //eslint-disable-next-line
    const fn = async (formData: FormData): Promise<any> => {
      try {
        const result = await universalServices.getData<{ notes: NoteType[] }>(
          "patches_note",
          "set-notes",
          formData,
        );

        if (result.success && result.data) {
          formNotesStore.clear();
          notesStore.appendNotes(result.data.notes);
        }
        return result;
      } catch (e) {
        const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";

        addNotification({ type: "error", message });
      }
    };

    this.setData(formData, fn);
  }
}

export const utilsNotes = new UtilsNotes();

class ChangesStore {
  final: boolean = false;
  endView: boolean = false;
  haveData: boolean = false;
  notes: Note[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  setOffHaveData = () => {
    this.haveData = false;
    this.setView();
    this.clear();
  };

  toggleFinalView = (view: boolean) => {
    this.final = view;
  };

  toggleEndView = (view: boolean) => {
    this.endView = view;
  };

  async setData(idUser: number) {
    try {
      const result = await universalServices.getData<{ notes: NoteType[] }>(
        "patches_note",
        "get-unsee-notes",
        { id: idUser },
      );

      if (result.success && result.data) {
        console.log(result.data);
        runInAction(() => {
          this.notes = result.data?.notes || [];
          if (this.notes.length > 0) this.haveData = true;
        });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
    }
  }

  async setView() {
    const noteIds = this.notes.map((note) => note.id);
    try {
      await universalServices.getData("patches_note", "set-view-notes", {
        user_id: userStore.id,
        note_ids: noteIds,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      console.log(message);
    }
  }

  clear() {
    this.notes = [];
    this.haveData = false;
    this.final = false;
    this.endView = false;
  }
}

export const changesStore = new ChangesStore();
